import React, { useEffect, useState } from 'react';
import Sidebar from '../sidebar';
import Topbar from '../topbar';
import styles from './layout.module.scss';
import Head from 'next/head';
import { csp } from '../../helpers/csp';
import { getHrefUrl } from '../../helpers/links';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { resolveMeta } from '../../helpers/metaUtils';
import AppConstants from '../../AppConstants';
import { isRuntimeSDE } from '../../hooks/useConfig';

interface LayoutProps {
  children: React.ReactElement;
  params?: any;
}

// eslint-disable-next-line react/prop-types
export default function Layout({ children, params }: LayoutProps) {
  const router = useRouter();
  const { t } = useTranslation('application');

  const { query, locale, asPath } = router;

  const [shouldSetCSP, setShouldSetCSP] = useState<boolean>(false);
  const [canonical, setCanonical] = useState<string>('https://flipp.com');

  useEffect(() => {
    if (router && !router.pathname.match('demo')) {
      setShouldSetCSP(true);
    }
    setCanonical(buildCanonical(locale, { ...params, ...query }));
  }, [router]);

  const [path] = asPath.split('?');
  const meta: any = resolveMeta(path || 'DEFAULT', query, locale, t, params);
  const { title, description, openGraph, buildCanonical } = meta;
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta
          content="width=device-width, height=device-height, initial-scale=1.0, viewport-fit=cover"
          name="viewport"
        />

        {process.env.NODE_ENV !== 'development' && shouldSetCSP ? (
          <meta httpEquiv="Content-Security-Policy" content={csp} />
        ) : null}
        {locale === 'en-us' ? (
          <link rel="alternate" hrefLang="en-us" href={getHrefUrl(router, 'en-us')} />
        ) : (
          <>
            <link rel="alternate" hrefLang="en-ca" href={getHrefUrl(router, 'en-ca')} />
            <link rel="alternate" hrefLang="fr-ca" href={getHrefUrl(router, 'fr-ca')} />
          </>
        )}
        <link rel="preconnect" href="https://f.wishabi.net" />
        <link rel="dns-prefetch" href="https://f.wishabi.net" />

        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={openGraph.getTitle ? openGraph.getTitle(query, t) : title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={openGraph.getImage(params)} />
        {params && params.store && (
          <meta name="thumbnail" content={`/assets/store-seo-thumbnails/${params.store}.webp`} />
        )}
        {/* robots 'noindex' meta tag should be absent on flipp.com, but present on other test subdomains*/}
        {process.env.NEXT_PUBLIC_HOSTNAME !== AppConstants.PRODUCTION_HOSTNAME ? (
          <meta name="robots" content="noindex" />
        ) : null}
      </Head>
      <div className={styles.layout}>
        {params && <Sidebar categories={params.categories} location={params.location} />}
        <div className={styles.content}>
          {!isRuntimeSDE() && <Topbar />}
          <main className={styles.main}>{children}</main>
        </div>
      </div>
    </>
  );
}
